import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAuthStore } from '../stores/authStore';
import { PermissionManager } from '../utils/permissionManager';
import { notify } from '../utils/notifications';
export const EnhancedPermissionWrapper = ({ permission, permissions, children, fallback = null, showFallback = false, requireAll = true, onUnauthorized, debugMode = false }) => {
    const { hasPermission, hasAllPermissions, hasAnyPermission, user } = useAuthStore();
    let hasAccess = false;
    let missingPermissions = [];
    if (permissions && permissions.length > 0) {
        if (requireAll) {
            hasAccess = hasAllPermissions(permissions);
            missingPermissions = permissions.filter(p => !hasPermission(p));
        }
        else {
            hasAccess = hasAnyPermission(permissions);
            missingPermissions = permissions.filter(p => !hasPermission(p));
        }
    }
    else {
        hasAccess = hasPermission(permission);
        missingPermissions = hasAccess ? [] : [permission];
    }
    if (debugMode) {
        console.log('🔐 Permission Check:', {
            user: user?.email,
            role: user?.role,
            permission: permission || permissions,
            hasAccess,
            missingPermissions,
            requireAll
        });
    }
    if (!hasAccess) {
        if (onUnauthorized) {
            onUnauthorized(missingPermissions);
        }
        return showFallback ? _jsx(_Fragment, { children: fallback }) : null;
    }
    return _jsx(_Fragment, { children: children });
};
// Specific guards for common use cases
export const ComponentGuard = ({ componentPermission, children, fallback, showPlaceholder = false }) => {
    const placeholder = showPlaceholder ? (_jsxs("div", { style: {
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px dashed rgba(255, 255, 255, 0.2)',
            padding: '16px',
            textAlign: 'center',
            color: '#666',
            fontSize: '12px'
        }, children: ["Component requires permission: ", componentPermission] })) : null;
    return (_jsx(EnhancedPermissionWrapper, { permission: componentPermission, fallback: fallback || placeholder, showFallback: !!(fallback || showPlaceholder), children: children }));
};
export const useActionPermission = (actionPermission) => {
    const { hasPermission, user } = useAuthStore();
    const canPerform = hasPermission(actionPermission);
    const performAction = (action, options) => {
        if (canPerform) {
            action();
        }
        else {
            console.warn(`🚫 Unauthorized action attempted:`, {
                action: actionPermission,
                user: user?.email,
                role: user?.role,
                roleLevel: user?.roleLevel
            });
            if (options?.showAlert !== false) {
                const permissionInfo = PermissionManager.getPermissionInfo(actionPermission);
                notify.warning('Access Denied', `You need "${permissionInfo.label}" permission to perform this action.`);
            }
            if (options?.onUnauthorized) {
                options.onUnauthorized();
            }
        }
    };
    return {
        canPerform,
        performAction,
        permissionInfo: PermissionManager.getPermissionInfo(actionPermission)
    };
};
export const useComponentPermission = (componentPermission) => {
    const { hasPermission } = useAuthStore();
    return {
        canShow: hasPermission(componentPermission),
        permissionInfo: PermissionManager.getPermissionInfo(componentPermission)
    };
};
export const ActionGuard = ({ actionPermission, children, onUnauthorized }) => (_jsx(EnhancedPermissionWrapper, { permission: actionPermission, onUnauthorized: () => onUnauthorized?.(), children: children }));
export const PermissionDebugger = ({ permission, permissions }) => {
    const { user, hasPermission, permissions: userPermissions } = useAuthStore();
    if (!user)
        return null;
    const permissionsToCheck = permissions || (permission ? [permission] : []);
    return (_jsxs("div", { style: {
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: '#000',
            color: '#fff',
            padding: 16,
            fontSize: 12,
            maxWidth: 300,
            zIndex: 9999,
            border: '1px solid #333'
        }, children: [_jsx("div", { children: _jsx("strong", { children: "Permission Debug" }) }), _jsxs("div", { children: ["User: ", user.email] }), _jsxs("div", { children: ["Role: ", user.role, " (Level ", user.roleLevel, ")"] }), _jsxs("div", { children: ["Total Permissions: ", userPermissions.length] }), permissionsToCheck.map(p => (_jsxs("div", { style: { color: hasPermission(p) ? '#22c55e' : '#ef4444' }, children: [hasPermission(p) ? '✓' : '✗', " ", p] }, p)))] }));
};
export const MultiPermissionGuard = ({ permissions, requireAll = true, children, fallback }) => (_jsx(EnhancedPermissionWrapper, { permission: permissions[0], permissions: permissions, requireAll: requireAll, fallback: fallback, showFallback: !!fallback, children: children }));
